/**
 * Forgot PIN recovery page.
 *
 * Allows a user who forgot their PIN to recover by entering their
 * 12-word recovery phrase. Verifies the mnemonic matches the current
 * wallet, then lets the user set a new PIN.
 *
 * Composition-only page: all logic lives in useForgotPin hook.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module ForgotPinPage
 */

import { MnemonicEntry } from '@/components/ui/mnemonic-entry';
import { PinSetup } from '@/components/ui/pin-setup';
import { useForgotPin } from '@/features/auth/hooks/useForgotPin';
import { Button } from '@/components/ui/button';

/** Props for ForgotPinPage. */
interface ForgotPinPageProps {
  /** Callback to navigate back (e.g., to lock screen). */
  readonly onBack?: () => void;
}

/**
 * Forgot PIN page component.
 *
 * Steps: enter mnemonic -> validate BIP-39 -> verify matches wallet ->
 * set new PIN -> re-encrypt mnemonic -> wallet unlocked.
 *
 * Tier: All tiers (core infrastructure).
 */
export default function ForgotPinPage({ onBack }: ForgotPinPageProps = {}): React.ReactElement {
  const { step, error, loading, submitMnemonic, submitNewPin } = useForgotPin();

  if (step === 'mnemonic_entry') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-background px-6">
        <div className="flex w-full max-w-md flex-col gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-xl font-semibold text-foreground">Reset your PIN</h1>
            <p className="text-sm text-muted-foreground">
              Enter your 12-word recovery phrase to verify your identity and set a new PIN.
            </p>
          </div>

          <MnemonicEntry
            onComplete={(mnemonic) => void submitMnemonic(mnemonic)}
            error={error}
            loading={loading}
          />

          {onBack ? (
            <Button variant="ghost" onClick={onBack} className="mt-2">
              Back to PIN entry
            </Button>
          ) : null}
        </div>
      </main>
    );
  }

  // step === 'pin_setup'
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold text-foreground">Set a new PIN</h1>
          <p className="text-sm text-muted-foreground">Choose a new 6-digit PIN for your wallet.</p>
        </div>

        <PinSetup onComplete={(pin) => void submitNewPin(pin)} loading={loading} />

        {error ? (
          <p className="text-sm text-destructive text-center" role="alert" aria-live="assertive">
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}
